/**
 * @file api.ts
 * @brief TanStack Query hooks for Super Admin → Enterprise inquiries.
 *
 * @author DES
 * @date 26-07-2026
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiGet, apiPatch } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import { toast } from "@/shared/hooks/useToast";
import {
  EnterpriseInquiryListResponseSchema,
  EnterpriseInquiryStatusSchema,
  type EnterpriseInquiryListParams,
  type EnterpriseInquiryListResponse,
  type EnterpriseInquiryStatus,
} from "./schemas";

export const enterpriseInquiryKeys = {
  all: ["admin", "enterprise-inquiries"] as const,
  list: (params: EnterpriseInquiryListParams) =>
    [...enterpriseInquiryKeys.all, "list", params] as const,
};

function buildQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v).length > 0) {
      usp.set(k, String(v));
    }
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

async function fetchEnterpriseInquiries(
  params: EnterpriseInquiryListParams,
): Promise<EnterpriseInquiryListResponse> {
  const url = `${adminEndpoints.enterpriseInquiries}${buildQuery({
    page: params.page,
    pageSize: params.pageSize,
    search: params.search,
    status: params.status || undefined,
  })}`;
  const raw = await apiGet<unknown>(url);
  return EnterpriseInquiryListResponseSchema.parse(raw);
}

export function useEnterpriseInquiriesList(
  params: EnterpriseInquiryListParams,
) {
  return useQuery({
    queryKey: enterpriseInquiryKeys.list(params),
    queryFn: () => fetchEnterpriseInquiries(params),
  });
}

export function useUpdateEnterpriseInquiryStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      status: EnterpriseInquiryStatus;
    }) => {
      EnterpriseInquiryStatusSchema.parse(args.status);
      return apiPatch<{ success?: boolean; inquiry?: { id: string; status: string } }>(
        adminEndpoints.enterpriseInquiryById(args.id),
        { status: args.status },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: enterpriseInquiryKeys.all });
      toast({ variant: "success", title: "Inquiry status updated" });
    },
    onError: (err: Error) => {
      toast({
        title: "Could not update status",
        description: err.message || "Could not update status",
      });
    },
  });
}
